import React, { useEffect, useState, useCallback } from 'react';
import { Plus, RefreshCw, Pencil, Trash2 } from 'lucide-react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { listChannels, createChannel, updateChannel, activateChannel, deactivateChannel, deleteChannel } from '../../utils/adminApi';

const EMPTY_FORM = {
  type: 'reseller',
  name: '',
  contact_email: '',
  commission_pct: 0,
  notes: '',
};

function toChannelId(type, name) {
  if (type === 'direct') return 'direct';
  return name.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

export default function ChannelsPage() {
  const { adminKey }            = useAdminAuth();
  const [channels, setChannels] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm]         = useState(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]       = useState('');
  const [actionError, setActionError] = useState('');

  // Edit state
  const [editTarget, setEditTarget]   = useState(null);
  const [editForm, setEditForm]       = useState({});
  const [editError, setEditError]     = useState('');
  const [editSaving, setEditSaving]   = useState(false);

  // Delete confirmation state
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting]         = useState(false);

  // Deactivate confirmation state
  const [toggleTarget, setToggleTarget] = useState(null);
  const [toggling, setToggling]         = useState(false);

  const directExists = channels.some(c => c.channel_id === 'direct');
  const derivedChannelId = toChannelId(form.type, form.name);

  const load = useCallback(() => {
    setError('');
    listChannels(adminKey)
      .then(setChannels)
      .catch(err => setError(err.message));
  }, [adminKey]);

  useEffect(() => { load(); }, [load]);

  async function confirmToggle() {
    setToggling(true);
    setActionError('');
    try {
      toggleTarget.is_active
        ? await deactivateChannel(adminKey, toggleTarget.channel_id)
        : await activateChannel(adminKey, toggleTarget.channel_id);
      setToggleTarget(null);
      load();
    } catch (err) {
      setActionError(err.message);
    } finally {
      setToggling(false);
    }
  }

  function openEdit(c) {
    setEditTarget(c);
    setEditForm({
      name:           c.name,
      contact_email:  c.contact_email  || '',
      commission_pct: c.commission_pct || 0,
      notes:          c.notes          || '',
    });
    setEditError('');
  }

  async function handleDelete() {
    setDeleting(true);
    setActionError('');
    try {
      await deleteChannel(adminKey, deleteTarget.channel_id);
      setDeleteTarget(null);
      load();
    } catch (err) {
      setActionError(err.message);
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  }

  async function handleUpdate(e) {
    e.preventDefault();
    if (!editForm.name.trim()) { setEditError('Name is required'); return; }
    const pct = Number(editForm.commission_pct);
    if (isNaN(pct) || pct < 0 || pct > 100) { setEditError('Commission must be 0–100'); return; }
    setEditError('');
    setEditSaving(true);
    try {
      await updateChannel(adminKey, editTarget.channel_id, {
        ...editForm,
        commission_pct: pct,
      });
      setEditTarget(null);
      load();
    } catch (err) {
      setEditError(err.message);
    } finally {
      setEditSaving(false);
    }
  }

  async function handleCreate(e) {
    e.preventDefault();
    setFormError('');
    if (form.type === 'direct' && directExists) {
      setFormError('A Direct channel already exists. Only one direct channel is needed.');
      return;
    }
    if (form.type === 'reseller' && !form.name.trim()) {
      setFormError('Reseller name is required');
      return;
    }
    setSubmitting(true);
    try {
      await createChannel(adminKey, {
        channel_id:     derivedChannelId,
        name:           form.type === 'direct' ? 'Direct Sales' : form.name,
        type:           form.type,
        contact_email:  form.contact_email,
        commission_pct: Number(form.commission_pct),
        notes:          form.notes,
      });
      setShowForm(false);
      setForm(EMPTY_FORM);
      load();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  if (error) return (
    <div className="admin-error-page">
      <p>{error}</p>
      <button className="admin-btn-secondary" onClick={load} style={{ marginTop: 12 }}>Retry</button>
    </div>
  );

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <h2 className="admin-page-title">Channels</h2>
        <div className="admin-page-actions">
          <button className="admin-btn-icon" onClick={load}><RefreshCw size={15} /></button>
          <button className="admin-btn-primary" onClick={() => setShowForm(true)}>
            <Plus size={15} /> Add Channel
          </button>
        </div>
      </div>

      {actionError && (
        <div className="admin-error" style={{ marginBottom: 12, padding: '8px 12px', borderRadius: 6, background: 'var(--clr-error-bg, #fef2f2)', border: '1px solid var(--clr-error, #ef4444)' }}>
          {actionError}
        </div>
      )}

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Channel ID</th><th>Name</th><th>Type</th>
              <th>Contact</th><th>Commission</th><th>Tenants</th><th>Status</th><th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {channels.length === 0 && (
              <tr><td colSpan={8} className="admin-table-empty">No channels yet</td></tr>
            )}
            {channels.map(c => (
              <tr key={c.channel_id}>
                <td><code>{c.channel_id}</code></td>
                <td>{c.name}</td>
                <td><span className={`admin-tag ${c.type}`}>{c.type}</span></td>
                <td>{c.contact_email || <span className="admin-muted">—</span>}</td>
                <td>{c.commission_pct > 0 ? `${c.commission_pct}%` : <span className="admin-muted">—</span>}</td>
                <td>{c.tenant_count}</td>
                <td><span className={`admin-badge ${c.is_active ? 'active' : 'inactive'}`}>{c.is_active ? 'Active' : 'Inactive'}</span></td>
                <td>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="admin-btn-sm" style={{ background: 'var(--clr-bg-elevated)', color: 'var(--clr-text-secondary)', border: '1px solid var(--clr-border)' }} onClick={() => openEdit(c)} title="Edit">
                      <Pencil size={12} />
                    </button>
                    {c.channel_id !== 'direct' && (
                      <button className={`admin-btn-sm ${c.is_active ? 'danger' : 'success'}`} onClick={() => setToggleTarget(c)}>
                        {c.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                    )}
                    {c.tenant_count === 0 && (
                      <button className="admin-btn-sm danger" onClick={() => setDeleteTarget(c)} title="Delete channel">
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Deactivate/Activate confirmation */}
      {toggleTarget && (
        <div className="admin-modal-backdrop" onClick={() => setToggleTarget(null)}>
          <div className="admin-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <h3 className="admin-modal-title">{toggleTarget.is_active ? 'Deactivate' : 'Activate'} Channel</h3>
            <p style={{ fontSize: 13, color: 'var(--clr-text-secondary)', lineHeight: 1.6, margin: '12px 0 20px' }}>
              {toggleTarget.is_active
                ? <>Deactivating <strong>{toggleTarget.name}</strong> will prevent new tenants from being assigned to it. Existing tenants are not affected.</>
                : <>Activate channel <strong>{toggleTarget.name}</strong> so it can be assigned to tenants.</>
              }
            </p>
            <div className="admin-form-actions">
              <button className="admin-btn-secondary" onClick={() => setToggleTarget(null)}>Cancel</button>
              <button className={`admin-btn-primary${toggleTarget.is_active ? ' danger' : ''}`} onClick={confirmToggle} disabled={toggling}>
                {toggling ? 'Saving…' : (toggleTarget.is_active ? 'Deactivate' : 'Activate')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      {deleteTarget && (
        <div className="admin-modal-backdrop" onClick={() => setDeleteTarget(null)}>
          <div className="admin-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <h3 className="admin-modal-title">Delete Channel</h3>
            <p style={{ fontSize: 13, color: 'var(--clr-text-secondary)', lineHeight: 1.6, margin: '12px 0 20px' }}>
              Permanently delete <strong>{deleteTarget.name}</strong> (<code>{deleteTarget.channel_id}</code>)?
              <br /><br />
              <span style={{ color: 'var(--clr-error, #ef4444)' }}>This cannot be undone.</span>
            </p>
            <div className="admin-form-actions">
              <button className="admin-btn-secondary" onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button className="admin-btn-primary danger" onClick={handleDelete} disabled={deleting}>
                {deleting ? 'Deleting…' : 'Yes, Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {editTarget && (
        <div className="admin-modal-backdrop" onClick={() => setEditTarget(null)}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <h3 className="admin-modal-title">Edit Channel — {editTarget.channel_id}</h3>
            <form onSubmit={handleUpdate} className="admin-form">
              <div className="admin-form-row">
                <label>Name *<input className="admin-input" value={editForm.name} onChange={e => setEditForm(f => ({...f, name: e.target.value}))} required /></label>
                <label>Commission %
                  <input type="number" className="admin-input" value={editForm.commission_pct} min={0} max={100} step={0.5}
                    onChange={e => setEditForm(f => ({...f, commission_pct: e.target.value}))} />
                </label>
              </div>
              <div className="admin-form-row">
                <label>Contact Email<input type="email" className="admin-input" value={editForm.contact_email} onChange={e => setEditForm(f => ({...f, contact_email: e.target.value}))} /></label>
              </div>
              <div className="admin-form-row">
                <label>Notes<textarea className="admin-input admin-textarea" value={editForm.notes} onChange={e => setEditForm(f => ({...f, notes: e.target.value}))} rows={2} /></label>
              </div>
              {editError && <p className="admin-error">{editError}</p>}
              <div className="admin-form-actions">
                <button type="button" className="admin-btn-secondary" onClick={() => setEditTarget(null)}>Cancel</button>
                <button type="submit" className="admin-btn-primary" disabled={editSaving}>{editSaving ? 'Saving…' : 'Save Changes'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create modal */}
      {showForm && (
        <div className="admin-modal-backdrop" onClick={() => { setShowForm(false); setForm(EMPTY_FORM); }}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <h3 className="admin-modal-title">Add Channel</h3>
            <p style={{ fontSize: 12, color: 'var(--clr-text-secondary)', marginBottom: 14, lineHeight: 1.5 }}>
              Channels are <strong>sales routes</strong>, not client companies.
              To add a client, go to <strong>Tenants</strong> and onboard them there.
            </p>
            <form onSubmit={handleCreate} className="admin-form">
              <div className="admin-form-row">
                <label>Channel Type *
                  <select className="admin-input" value={form.type} onChange={e => setForm(f => ({...f, type: e.target.value, name: '', commission_pct: 0, contact_email: '', notes: ''}))}>
                    <option value="reseller">Reseller — a partner who sells on your behalf (e.g. Wipro, IBM)</option>
                    <option value="direct">Direct — you sell directly to the client</option>
                  </select>
                </label>
              </div>

              {form.type === 'direct' ? (
                <div className="admin-form-row">
                  <div style={{ padding: '10px 12px', background: 'var(--clr-bg-elevated)', borderRadius: 6, border: '1px solid var(--clr-border)', fontSize: 13, color: 'var(--clr-text-secondary)', width: '100%' }}>
                    Channel ID will be <code>direct</code>.
                    {directExists && <span style={{ color: 'var(--clr-warning, #f59e0b)', display: 'block', marginTop: 6 }}>⚠ A direct channel already exists.</span>}
                  </div>
                </div>
              ) : (
                <>
                  <div className="admin-form-row">
                    <label>Reseller Company Name *
                      <input className="admin-input" value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} placeholder="e.g. Wipro Limited" />
                    </label>
                    <label>Channel ID (auto)
                      <input className="admin-input" value={derivedChannelId} readOnly style={{ opacity: 0.6, cursor: 'default' }} />
                    </label>
                  </div>
                  <div className="admin-form-row">
                    <label>Commission %
                      <input type="number" className="admin-input" value={form.commission_pct} min={0} max={100} step={0.5} onChange={e => setForm(f => ({...f, commission_pct: e.target.value}))} />
                    </label>
                    <label>Contact Email
                      <input type="email" className="admin-input" value={form.contact_email} onChange={e => setForm(f => ({...f, contact_email: e.target.value}))} placeholder="partner@wipro.com" />
                    </label>
                  </div>
                  <div className="admin-form-row">
                    <label>Notes<textarea className="admin-input admin-textarea" value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} rows={2} /></label>
                  </div>
                </>
              )}

              {formError && <p className="admin-error">{formError}</p>}
              <div className="admin-form-actions">
                <button type="button" className="admin-btn-secondary" onClick={() => { setShowForm(false); setForm(EMPTY_FORM); }}>Cancel</button>
                <button type="submit" className="admin-btn-primary" disabled={submitting || (form.type === 'direct' && directExists)}>
                  {submitting ? 'Creating…' : 'Create Channel'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
